import { html, raw } from '../lib/html.js';
import { qrSvg } from '../lib/qr.js';
import { busy, confirm, toast, toastError, toastSuccess } from '../lib/ui.js';
import { authorizations, countdown, DURATIONS, fmtDateTime, spacedCode } from '../services/authorizations.js';
import { Page, go } from './base.js';

/**
 * /vehicles/:vehicleId/autorizar — o dono gera o código para a oficina.
 *   1. escolhe: qualquer oficina (a primeira que usar) ou uma oficina específica; e o prazo de acesso
 *   2. mostra QR + código de 6 dígitos (30 min, uso único) e acompanha até a oficina ativar
 */
class VehicleAuthorizePage extends Page {
  template() {
    return html`
      <ion-header translucent>
        <ion-toolbar>
          <ion-buttons slot="start"><ion-back-button default-href="/home" text="Voltar"></ion-back-button></ion-buttons>
          <ion-title>Autorizar empresa</ion-title>
        </ion-toolbar>
      </ion-header>
      <ion-content class="ion-padding" fullscreen>
        <div class="narrow body"></div>
      </ion-content>`;
  }

  setup() {
    this.addEventListener('ionViewWillLeave', () => this.stopTimers());
    this.$('.body').addEventListener('click', (e) => {
      const el = e.target.closest('[data-action]');
      if (el) this.action(el.dataset.action, el);
    });
  }

  get vid() {
    if (this.vehicleId) return String(this.vehicleId);
    const m = location.pathname.match(/\/vehicles\/([^/?#]+)/);
    return m ? decodeURIComponent(m[1]) : '';
  }

  onEnter() {
    this.stopTimers();
    this.state = { type: 'GENERIC', durationDays: 7, workshop: null, results: [] };
    this.current = null;
    this.renderChoose();
  }

  stopTimers() {
    clearInterval(this.tick);
    clearInterval(this.poll);
    clearTimeout(this.searchTimer);
  }

  renderChoose() {
    const s = this.state;
    this.$('.body').innerHTML = html`
      <h3 class="section-title">Qual empresa?</h3>
      <div class="segmented" role="radiogroup">
        <button type="button" role="radio" aria-checked="${s.type === 'GENERIC'}" class="${s.type === 'GENERIC' ? 'on' : ''}" data-action="type" data-value="GENERIC">Qualquer uma</button>
        <button type="button" role="radio" aria-checked="${s.type === 'SPECIFIC'}" class="${s.type === 'SPECIFIC' ? 'on' : ''}" data-action="type" data-value="SPECIFIC">Uma específica</button>
      </div>
      <p class="hint small">${s.type === 'GENERIC' ? 'A primeira empresa aprovada que usar o código fica autorizada.' : 'O código só funciona na empresa escolhida.'}</p>
      ${s.type === 'SPECIFIC'
        ? html`
          ${s.workshop
            ? html`<ion-item lines="full" class="picked">
                <ion-icon slot="start" name="storefront-outline"></ion-icon>
                <ion-label><h3>${s.workshop.name}</h3><p>${[s.workshop.city, s.workshop.state].filter(Boolean).join('/')} · CNPJ ${s.workshop.cnpjFormatted}</p></ion-label>
                <ion-button slot="end" fill="clear" data-action="unpick">Trocar</ion-button>
              </ion-item>`
            : html`
              <ion-input class="search" label="Nome ou CNPJ da empresa" label-placement="floating" fill="outline" clear-input="true"></ion-input>
              <ion-list class="results">
                ${s.results.map((w) => html`
                  <ion-item button data-action="pick" data-id="${w.id}">
                    <ion-label><h3>${w.name}</h3><p>${[w.city, w.state].filter(Boolean).join('/')} · CNPJ ${w.cnpjFormatted}</p></ion-label>
                  </ion-item>`)}
              </ion-list>
              ${s.searched && !s.results.length ? html`<p class="hint small">Nenhuma empresa aprovada encontrada.</p>` : ''}`}`
        : ''}

      <h3 class="section-title">Por quanto tempo?</h3>
      <div class="segmented four" role="radiogroup">
        ${DURATIONS.map((d) => html`<button type="button" role="radio" aria-checked="${s.durationDays === d}" class="${s.durationDays === d ? 'on' : ''}" data-action="duration" data-value="${d}">${d} ${d === 1 ? 'dia' : 'dias'}</button>`)}
      </div>
      <p class="hint small">Depois de ativado, a empresa acessa o veículo por esse prazo. Você pode revogar antes a qualquer momento.</p>
      <ion-button expand="block" class="submit ion-margin-top" data-action="generate" ${s.type === 'SPECIFIC' && !s.workshop ? 'disabled' : ''}>
        <ion-icon slot="start" name="qr-code-outline"></ion-icon>Gerar código
      </ion-button>`.value;

    const search = this.$('ion-input.search');
    if (search) {
      search.value = s.query ?? '';
      search.addEventListener('ionInput', (e) => {
        s.query = String(e.target.value ?? '').trim();
        clearTimeout(this.searchTimer);
        if (s.query.length < 2) return;
        this.searchTimer = setTimeout(() => this.search(), 350);
      });
    }
  }

  async search() {
    const s = this.state;
    try {
      s.results = (await authorizations.searchWorkshops(s.query)).items;
      s.searched = true;
      this.renderChoose();
      this.$('ion-input.search')?.setFocus();
    } catch (err) {
      toastError(err.message);
    }
  }

  async action(name, el) {
    const s = this.state;
    switch (name) {
      case 'type':
        s.type = el.dataset.value;
        return this.renderChoose();
      case 'duration':
        s.durationDays = Number(el.dataset.value);
        return this.renderChoose();
      case 'pick':
        s.workshop = s.results.find((w) => w.id === el.dataset.id) ?? null;
        return this.renderChoose();
      case 'unpick':
        s.workshop = null;
        return this.renderChoose();
      case 'generate':
        return busy(el, async () => {
          try {
            const { authorization } = await authorizations.create(this.vid, {
              type: s.type,
              durationDays: s.durationDays,
              ...(s.type === 'SPECIFIC' ? { workshopId: s.workshop.id } : {}),
            });
            this.current = authorization;
            this.renderCode();
          } catch (err) {
            toastError(err.message);
          }
        });
      case 'cancel': {
        const sure = await confirm({ header: 'Cancelar código?', message: 'O código deixa de funcionar.', confirmText: 'Cancelar código', cancelText: 'Voltar', destructive: true });
        if (!sure) return;
        try {
          await authorizations.revoke(this.vid, this.current.id);
          toast('Código cancelado.');
        } catch {
          /* já pode ter sido usado ou vencido */
        }
        return go(`/vehicles/${this.vid}`, 'back');
      }
      case 'done':
        return go(`/vehicles/${this.vid}`, 'back');
      case 'again':
        return this.onEnter();
    }
  }

  renderCode() {
    const a = this.current;
    this.$('.body').innerHTML = html`
      <div class="auth-code">
        <p class="hint">${a.type === 'SPECIFIC' ? html`Para <strong>${a.workshop?.name}</strong>` : 'Para qualquer empresa aprovada'} · acesso por ${a.durationDays} ${a.durationDays === 1 ? 'dia' : 'dias'}</p>
        <div class="qr">${raw(qrSvg(a.qr))}</div>
        <p class="code-label">ou digite o código</p>
        <p class="code" aria-label="Código ${a.code.split('').join(' ')}">${spacedCode(a.code)}</p>
        <p class="timer">Vale por <strong class="left">${countdown(a.codeExpiresAt)}</strong> · uso único</p>
        <p class="hint small">Mostre ao profissional. Ele lê o QR ou digita o código no app TicketCar.</p>
        <ion-button expand="block" fill="clear" color="medium" data-action="cancel">Cancelar código</ion-button>
      </div>`.value;

    this.tick = setInterval(() => {
      const left = this.$('.left');
      if (left) left.textContent = countdown(a.codeExpiresAt);
      if (new Date(a.codeExpiresAt) <= Date.now()) this.renderExpired();
    }, 1000);
    // acompanha até a oficina ativar
    this.poll = setInterval(async () => {
      try {
        const { items } = await authorizations.list(this.vid);
        const now = items.find((x) => x.id === a.id);
        if (now?.status === 'ACTIVE') this.renderActivated(now);
        else if (now && now.status !== 'PENDING') this.renderExpired();
      } catch {
        /* tenta de novo no próximo ciclo */
      }
    }, 3000);
  }

  renderActivated(a) {
    this.stopTimers();
    toastSuccess(`${a.workshop?.name ?? 'Empresa'} autorizada!`);
    this.$('.body').innerHTML = html`
      <div class="center">
        <ion-icon name="checkmark-circle" class="big-ok"></ion-icon>
        <h2>${a.workshop?.name ?? 'Empresa'} autorizada</h2>
        <p class="hint">${a.activatedBy ? html`Ativado por ${a.activatedBy.name}. ` : ''}Acesso até ${fmtDateTime(a.expiresAt)}.</p>
        <ion-button expand="block" data-action="done">Voltar ao veículo</ion-button>
      </div>`.value;
  }

  renderExpired() {
    this.stopTimers();
    this.$('.body').innerHTML = html`
      <div class="center">
        <ion-icon name="time-outline" class="empty-icon"></ion-icon>
        <h2>O código expirou</h2>
        <p class="hint">Ninguém usou o código nos 30 minutos. Gere outro quando estiver com o profissional.</p>
        <ion-button expand="block" data-action="again">Gerar outro código</ion-button>
      </div>`.value;
  }
}

customElements.define('page-vehicle-authorize', VehicleAuthorizePage);
